import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OwnershipLevel } from '@fitness-force/enum';
import { AppErrors, BadRequestError, NotFoundError } from '@fitness-force/models';;
import type { EmailTemplateMasterById, EmailTemplateMasterResponse } from '@fitness-force/models';
import { environment } from '@fitness-force/environments';
import type { EmailFromMasterResponse } from '@fitness-force/models';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { throwError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TemplateManagementService {
  private http = inject(HttpClient);
  private router = inject(Router);


  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  getEmailFromConfig(tenantId: number): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_FROM_CONFIG
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
  getSingleEmailFromConfig(
    tenantId: number,
    fromEmailId: number
  ): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_FROM_CONFIG_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{from_email_id}', fromEmailId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  createProviderConfig(
    tenantId: number,
    email_provider_config_master: number,
    data: any
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.CREATE_EMAIL_FROM_CONFIG
      .replace('{tenant_id}', tenantId.toString())
      .replace('{email_provider_config_master}', email_provider_config_master.toString());
    return this.http
      .post(url, data, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateProviderConfig(
    tenantId: number,
    email_provider_config_master: number,
    emailFromId: number,
    data: any
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_EMAIL_FROM_CONFIG
      .replace('{tenant_id}', tenantId.toString())
      .replace('{email_provider_config_master}', email_provider_config_master.toString())
      .replace('{email_from_id}', emailFromId.toString());
    return this.http
      .put(url, data, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getEmailProviderConfig(tenantId: number): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_PROVIDER_CONFIG
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getEmailTemplate(tenantId: number): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_TEMPLATES
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getSingleEmailTemplate(
    tenantId: number,
    templateId: number
  ): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_TEMPLATE_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{template_id}', templateId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  createEmailTemplate(ownership:OwnershipLevel, payload:any, tenantId:number, franchiseGroupId:number) {

    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.CREATE_EMAIL_TEMPLATE;
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.CREATE_EMAIL_TEMPLATE
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.CREATE_EMAIL_TEMPLATE
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.CREATE_EMAIL_TEMPLATE
          .replace('{tenant_id}', tenantId.toString());
        break;
    }

    return this.http
      .post(
        url,
        payload,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateEmailTemplate(ownership:OwnershipLevel, templateId:number, updatePayload:any, tenantId:number, franchiseGroupId:number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.UPDATE_EMAIL_TEMPLATE
          .replace('{template_id}', templateId.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.UPDATE_EMAIL_TEMPLATE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', templateId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_EMAIL_TEMPLATE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', templateId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_EMAIL_TEMPLATE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', templateId.toString());
        break;
    }

    return this.http.put(url,updatePayload,this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getEmailNotifications(tenantId: number): Observable<any> {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_NOTIFICATIONS
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(url, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateEmailFrom(tenantId: number, fromId: number, data: any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.UPDATE_EMAIL_FROM_LEGACY
      .replace('{tenant_id}', tenantId.toString())
      .replace('{from_id}', fromId.toString());
    return this.http
      .put(url, data, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markActiveFromEmail(
    tenantId: number,
    emailProvider: number,
    emailFrom: number
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_EMAIL_FROM_ACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{email_provider}', emailProvider.toString())
      .replace('{email_from}', emailFrom.toString());
    return this.http
      .post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveFromEmail(
    tenantId: number,
    emailProvider: number,
    emailFrom: number
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_EMAIL_FROM_INACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{email_provider}', emailProvider.toString())
      .replace('{email_from}', emailFrom.toString());
    return this.http
      .post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markActiveTemplate(tenantId: number, email_template_master_id: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_EMAIL_TEMPLATE_ACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{template_id}', email_template_master_id.toString());
    return this.http
      .post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveTemplate(tenantId: number, email_template_master_id: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_EMAIL_TEMPLATE_INACTIVE
      .replace('{tenant_id}', tenantId.toString())
      .replace('{template_id}', email_template_master_id.toString());
    return this.http
      .post(url, {}, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveTemplateByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number, email_template_master_id: number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.MARK_EMAIL_TEMPLATE_INACTIVE
          .replace('{template_id}', email_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.MARK_EMAIL_TEMPLATE_INACTIVE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', email_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_EMAIL_TEMPLATE_INACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', email_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_EMAIL_TEMPLATE_INACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', email_template_master_id.toString());
        break;
    }
    return this.http.post(url, {}, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  } 

  markActiveTemplateByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number, email_template_master_id: number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.MARK_EMAIL_TEMPLATE_ACTIVE
          .replace('{template_id}', email_template_master_id.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.MARK_EMAIL_TEMPLATE_ACTIVE
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', email_template_master_id.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_EMAIL_TEMPLATE_ACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', email_template_master_id.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.MARK_EMAIL_TEMPLATE_ACTIVE
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', email_template_master_id.toString());
        break;
    }
    return this.http.post(url, {}, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  } 


  // neeed to remove its duplicate from notification service
  getEmailTemplatesByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number): Observable<EmailTemplateMasterResponse> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_EMAIL_TEMPLATES;
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_EMAIL_TEMPLATES
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_TEMPLATES
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_TEMPLATES
          .replace('{tenant_id}', tenantId.toString());
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getEmailTemplateByIdWithOwnership(ownership:OwnershipLevel, templateId:number,franchiseGroupId:number,tenantId:number): Observable<EmailTemplateMasterById> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_EMAIL_TEMPLATE_BY_ID
          .replace('{template_id}', templateId.toString());
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_EMAIL_TEMPLATE_BY_ID
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{template_id}', templateId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_TEMPLATE_BY_ID
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', templateId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_TEMPLATE_BY_ID
          .replace('{tenant_id}', tenantId.toString())
          .replace('{template_id}', templateId.toString());
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getSendEmailFromListByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number): Observable<EmailFromMasterResponse> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_EMAIL_FROM_CONFIGS;
        break;
      case OwnershipLevel.Franchise:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.FRANCHISE_GROUP.GET_EMAIL_FROM_CONFIGS
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_FROM_CONFIG
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = environment.BASE_URL + CONST_URL_SEGEMENTS.NOTIFICATIONS.GET_EMAIL_FROM_CONFIG
          .replace('{tenant_id}', tenantId.toString());
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }
}
